/**
 * User Signals for Probabilistic Matching
 * Collects non-invasive browser signals for user matching
 */

export interface UserSignals {
  timezone: string;
  language: string;
  screenWidth: number;
  screenHeight: number;
  timestamp: number;
}

/**
 * Collect user signals for matching
 * These are privacy-friendly and don't identify individuals
 */
export function collectUserSignals(): UserSignals {
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    timestamp: Date.now(),
  };
}

/**
 * Generate a fingerprint from signals
 * Used for quick lookups
 */
export function generateFingerprint(signals: UserSignals): string {
  const { timezone, language, screenWidth, screenHeight } = signals;
  return `${timezone}:${language}:${screenWidth}x${screenHeight}`;
}

/**
 * Calculate match confidence between two signal sets
 * Returns 0-100 score
 */
export function calculateMatchConfidence(
  signals1: UserSignals,
  signals2: UserSignals
): number {
  let score = 0;
  
  // Exact timezone match: +40 points
  if (signals1.timezone === signals2.timezone) score += 40;
  
  // Exact language match: +20 points
  if (signals1.language === signals2.language) score += 20;
  
  // Exact screen size match: +30 points
  if (
    signals1.screenWidth === signals2.screenWidth &&
    signals1.screenHeight === signals2.screenHeight
  ) {
    score += 30;
  }
  
  // Timing proximity: +10 points if within 30 days
  const daysDiff = Math.abs(signals1.timestamp - signals2.timestamp) / (1000 * 60 * 60 * 24);
  if (daysDiff <= 30) score += 10;
  
  return score;
}

